import { api } from './database';
import { 
  Form990Data as ServiceForm990Data, 
  PartIVResponse, 
  Form990QuestionnaireSubmission,
  Officer as ServiceOfficer
} from '../types/Form990Types';
import { validateForm990, form990PDFUtils } from '../utils/validators';
import { Form990PDFHandler } from './Form990PDFHandler';
import axios from 'axios';
import { BaseService } from './BaseService';

// Import the form990API object instead of just the generateForm990PDF function
import { form990API } from '../../pages/api/templates/form990';

// Define the PDF handler's expected types locally if they're not properly exported
interface PDFOfficer {
  name: string;
  title: string;
  averageHoursPerWeek: number;
  compensation: number;
  role: "DIRECTOR" | "OFFICER" | "BOTH";
}

interface PDFForm990Data {
  // Define the structure expected by the PDF handler
  organizationName?: string;
  ein?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  officers?: PDFOfficer[];
  // Add other required fields
}

interface Form990QuestionnaireData {
  partIV: PartIVResponse[];
  // Add other parts as needed
}

export interface Form990QuestionData {
  fiscalSponsorId: string;
  year: number;
  dataPath: string;
}

export interface PartIVQuestionnaireProps {
  responses: Record<string, any>;
  onResponseChange: (newResponses: Record<string, any>) => void;
  autoPopulatedFields: string[];
}

interface Form990Response {
  organizationId: string;
  responses: any;
  submittedAt?: Date;
  status?: string;
}

export class Form990Service extends BaseService {
  private pdfHandler: Form990PDFHandler;

  constructor() {
    super();
    // Use the static create method instead of constructor if it's private
    this.pdfHandler = Form990PDFHandler.create();
  }

  // Convert between the types
  private convertToHandlerFormat(data: ServiceForm990Data): PDFForm990Data {
    // Convert the data format
    return {
      ...data,
      officers: data.officers?.map(officer => {
        // Map the officer properties correctly based on the actual structure
        // of ServiceOfficer and what PDFOfficer expects
        return {
          name: officer.name || '',
          title: officer.title || '',
          // Use default values since the properties might not exist on the source object
          averageHoursPerWeek: 0, // Default value
          compensation: 0, // Default value
          role: "OFFICER" as "DIRECTOR" | "OFFICER" | "BOTH" // Default value with type assertion
        } as PDFOfficer;
      })
    };
  }

  // Helper method to map string roles to the expected enum values
  private mapOfficerRole(role: string): "DIRECTOR" | "OFFICER" | "BOTH" {
    const normalizedRole = role.toUpperCase();
    if (normalizedRole.includes('DIRECTOR') && normalizedRole.includes('OFFICER')) {
      return "BOTH";
    } else if (normalizedRole.includes('DIRECTOR')) {
      return "DIRECTOR";
    } else {
      return "OFFICER";
    }
  }

  async generatePreview(data: ServiceForm990Data): Promise<ArrayBuffer> {
    try {
      console.log('Form990Service generating preview');
      
      // Normalize data if needed
      const normalizedData = this.normalizeData(data);
      
      // Convert to the format expected by the PDF handler
      const convertedData = this.convertToHandlerFormat(normalizedData);
      
      // Use type assertion to tell TypeScript this is the correct type
      // This is necessary because we're adapting between incompatible types
      const pdfBuffer = await this.pdfHandler.generateForm990(convertedData as any);
      
      // Explicitly convert Uint8Array to ArrayBuffer if needed and use type assertion
      if (pdfBuffer instanceof Uint8Array) {
        return pdfBuffer.buffer as ArrayBuffer;
      } else {
        return pdfBuffer as ArrayBuffer;
      }
    } catch (error) {
      console.error('Form990Service generatePreview error:', error);
      throw error;
    }
  }

  private normalizeData(data: ServiceForm990Data): ServiceForm990Data {
    // Implementation of data normalization
    return data;
  }

  async submitForm(formData: ServiceForm990Data): Promise<void> {
    try {
      // Validate form data
      const validationErrors = validateForm990(formData);
      if (Object.keys(validationErrors).length > 0) {
        throw new Error(`Validation failed: ${Object.values(validationErrors).join(', ')}`);
      }

      // Convert to the format expected by the PDF handler
      const convertedData = this.convertToHandlerFormat(formData);

      // Generate final PDF - use type assertion to bypass type checking
      const pdfBuffer = await this.pdfHandler.generateForm990(convertedData as any);

      // Submit to API
      await api.post('/form990/submit', {
        data: formData,
        pdf: pdfBuffer
      }, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error('Form990Service submitForm error:', error);
      throw error;
    }
  }

  async validateData(formData: ServiceForm990Data): Promise<Record<string, string>> {
    return validateForm990(formData);
  }

  async getFieldValue(formData: ServiceForm990Data, fieldPath: string): Promise<any> {
    return form990PDFUtils.getFieldValue(formData, fieldPath);
  }

  async updateFieldValue(formData: ServiceForm990Data, fieldPath: string, value: any): Promise<ServiceForm990Data> {
    const updatedData = { ...formData };
    const pathParts = fieldPath.split('.');
    let current: any = updatedData;

    // Navigate to the correct nested object
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }

    // Update the value
    current[pathParts[pathParts.length - 1]] = value;

    // Validate the updated data
    const validationErrors = validateForm990(updatedData);
    if (Object.keys(validationErrors).length > 0) {
      console.warn('Validation warnings after update:', validationErrors);
    }

    return updatedData;
  }

  async calculateTotals(formData: ServiceForm990Data): Promise<ServiceForm990Data> {
    const updatedData = { ...formData };

    // Calculate revenue totals
    if (updatedData.revenue) {
      updatedData.revenue.total = Object.values(updatedData.revenue)
        .filter(val => typeof val === 'number')
        .reduce((sum, val) => sum + val, 0);
    }

    // Calculate expense totals
    if (updatedData.expenses) {
      updatedData.expenses.total = Object.values(updatedData.expenses)
        .filter(val => typeof val === 'number')
        .reduce((sum, val) => sum + val, 0);
    }

    // Calculate balance sheet totals
    if (updatedData.part_x?.assets?.end) {
      updatedData.part_x.assets.end.total = 
        (updatedData.part_x.assets.end.current || 0) +
        (updatedData.part_x.assets.end.investments || 0) +
        (updatedData.part_x.assets.end.fixed || 0);
    }

    return updatedData;
  }

  async getRequiredSchedules(formData: ServiceForm990Data): Promise<string[]> {
    const requiredSchedules: string[] = [];

    // Check Schedule A (always required for 501(c)(3))
    if (formData.taxExemptStatus?.c501c3) {
      requiredSchedules.push('A');
    }

    // Check Schedule B (contributions > $5000)
    const contributionsTotal = formData.revenue?.contributions?.total ?? 0;
    if (contributionsTotal > 5000) {
      requiredSchedules.push('B');
    }

    // Add more schedule requirements based on your rules

    return requiredSchedules;
  }

  private processPartIVResponses(responses: PartIVResponse[]): void {
    // Map responses to PDF form fields
    responses.forEach(response => {
      const fieldMapping = this.getPartIVFieldMapping(response.question);
      if (fieldMapping) {
        this.setFormFieldValue(fieldMapping, response.response);
      }
    });
  }

  private getPartIVFieldMapping(question: number): string {
    // Map question numbers to PDF form field names
    const mappings: Record<number, string> = {
      1: 'topmostSubform[0].Page4[0].c4_1[0]',
      2: 'topmostSubform[0].Page4[0].c4_2[0]',
      // Add more mappings as needed
    };
    return mappings[question];
  }

  private setFormFieldValue(fieldPath: string, value: any): void {
    // Implementation of setting form field value
    // This could interact with your PDF handling logic
    console.log(`Setting field ${fieldPath} to value ${value}`);
  }

  async getQuestionData({ fiscalSponsorId, year, dataPath }: Form990QuestionData): Promise<any> {
    try {
      const response = await form990API.getDraft(fiscalSponsorId);
      return response.data?.questionnaire?.responses?.[dataPath] || null;
    } catch (error) {
      console.error('Error fetching question data:', error);
      throw error;
    }
  }

  async submitQuestionnaire(data: Form990QuestionnaireSubmission): Promise<any> {
    return form990API.submitQuestionnaire(data);
  }

  async getDraft(fiscalSponsorId: string): Promise<any> {
    return form990API.getDraft(fiscalSponsorId);
  }

  async generateForm990(formData: any): Promise<any> {
    try {
      // Use the client-side function
      return await form990API.generateForm990PDF(formData);
    } catch (error) {
      console.error('Error in Form990Service:', error);
      throw error;
    }
  }
  
  async getForm990Templates(): Promise<any[]> {
    try {
      const response = await axios.get('/api/templates/form990');
      return response.data;
    } catch (error) {
      console.error('Error fetching Form 990 templates:', error);
      return [];
    }
  }

  async saveResponses(organizationId: string, responses: any): Promise<boolean> {
    try {
      const query = `
        INSERT INTO sponsfi.form990_responses (
          organization_id,
          responses,
          submitted_at,
          status
        ) VALUES ($1, $2, NOW(), $3)
        ON CONFLICT (organization_id) 
        DO UPDATE SET
          responses = $2,
          submitted_at = NOW(),
          status = $3
        RETURNING id
      `;
      
      const result = await this.executeQuery(query, [
        organizationId,
        JSON.stringify(responses),
        'submitted'
      ]);
      
      return !!result.rows?.[0]?.id;
    } catch (error) {
      console.error('Error saving Form 990 responses:', error);
      return false;
    }
  }
  
  async getResponses(organizationId: string): Promise<any> {
    try {
      const query = `
        SELECT responses, submitted_at, status
        FROM sponsfi.form990_responses
        WHERE organization_id = $1
        ORDER BY submitted_at DESC
        LIMIT 1
      `;
      
      const result = await this.executeQuery(query, [organizationId]);
      
      if (result.rows?.[0]) {
        const { responses, submitted_at, status } = result.rows[0];
        return {
          responses: typeof responses === 'string' ? JSON.parse(responses) : responses,
          submittedAt: submitted_at,
          status
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting Form 990 responses:', error);
      return null;
    }
  }
  
  async getQuestionnaire(fiscalSponsorId: string): Promise<any> {
    try {
      const query = `
        SELECT form990_data
        FROM sponsfi.fiscal_sponsors
        WHERE id = $1
      `;
      
      const result = await this.executeQuery(query, [fiscalSponsorId]);
      
      if (result.rows?.[0]?.form990_data) {
        const form990Data = result.rows[0].form990_data;
        return {
          success: true,
          data: {
            ...form990Data.questionnaire,
            form990Data: {
              organizationInfo: form990Data.organizationInfo
            }
          },
          message: 'Questionnaire retrieved successfully'
        };
      }
      
      return {
        success: false,
        message: 'Questionnaire not found'
      };
    } catch (error) {
      console.error('Error getting Form 990 questionnaire:', error);
      return {
        success: false,
        message: 'Error retrieving questionnaire'
      };
    }
  }
} 